import { ThemeLoaderConfig } from './theme-loader-config';
import { appConfig, userData } from '../config';

/**
 * Poseidon Theme loader service. Applies the theme selected by the user to Poseidon Core or the application.
 */
export class ThemeLoaderService {
  private config: ThemeLoaderConfig;
  private activeThemeInternal: string;
  private themeSwitchId = 0;

  /**
   * removeLinkDelay property, delay in ms before like removal on theme switching
   */
  removeLinkDelay = 1000;

  /**
   * PosThemeLoaderService constructor
   */
  constructor(config: ThemeLoaderConfig) {
    this.config = config;
    /* istanbul ignore next else */
    if (config.activeTheme) {
      this.activeThemeInternal = config.activeTheme;
      document.documentElement.classList.add(this.activeTheme);
    }
  }

  /**
   * Returns active theme.
   *
   * @return Name of active theme
   */
  get activeTheme(): string {
    return this.activeThemeInternal;
  }

  /**
   * Stores active theme and runs theme switch if value has been changed.
   *
   * @param name Name of active theme
   */
  set activeTheme(name: string) {
    if (this.activeThemeInternal === name || this.config.themes[name] === undefined) {
      return;
    }
    const currentTheme = this.activeThemeInternal;

    this.applyTheme(name).then(() => {
      document.documentElement.classList.add(this.activeThemeInternal);
      document.documentElement.classList.remove(currentTheme);
      document.documentElement.dispatchEvent(new CustomEvent('updateTheme', { detail: this.activeTheme }))
    }, () => false);
  }

  /**
   * Applies the current theme by adding a new link element.
   * Removes the old link elements once all new styles have finished loading.
   *
   * @param name Name of active theme
   *
   * @return Returns a promise which will be resolved once the old link elements were removed
   */
  private applyTheme(name: string): Promise<any> {
    const deferred: any = {
      promise: undefined,
      resolve: undefined,
      reject: undefined,
    };

    deferred.promise = new Promise((resolve, reject) => {
      deferred.resolve = resolve;
      deferred.reject = reject;
    });

    const id = ++this.themeSwitchId;

    this.loadCss({
      'href': `${this.config.baseUrl}${this.config.themes[name]}`,
      'data-theme': name,
      'role': 'theme',
    }).then(() => {
      // remove old link elements asynchronously to minimize layout flashing
      window.setTimeout(() => {
        // check if current theme switch is the very last one
        if (id !== this.themeSwitchId) {
          deferred.reject(
            `Concurrent theme switch identified! Switch id should be ${this.themeSwitchId}, but found ${id}`);
          return;
        }
        this.activeThemeInternal = name;
        this.removeInactiveThemeStyles();
        deferred.resolve();
      }, this.removeLinkDelay);
    }, (error) => {
      deferred.reject(error);
    });

    return deferred.promise;
  }

  /**
   * Removes all theme related link elements except the active ones.
   */
  private removeInactiveThemeStyles() {
    Array.from(document.querySelectorAll('link')).forEach((element) => {
      if (element.getAttribute('role') === 'theme'
        && element.getAttribute('data-theme') !== this.activeThemeInternal) {
        element.parentNode.removeChild(element);
      }
    });
  }

  /**
   * Creates a link element with the given DOM attributes and appends it to the documentElement.
   * Returns a promise that will be resolved with the created element when the style sheet has finished loading or
   * rejected with an error if it could not be loaded.
   *
   * @param attributes Map of DOM attributes to set. Must include 'href'
   * @returns Returns a promise which will be resolved link will be loaded
   */
  public loadCss(attributes: { href: string, [propName: string]: any }): Promise<Element> {
    const linkElement = document.createElement('link');

    linkElement.setAttribute('rel', 'stylesheet');
    linkElement.setAttribute('type', 'text/css');

    for (const name in attributes) {
      /* istanbul ignore next else */
      if (Object.prototype.hasOwnProperty.call(attributes, name)) {
        linkElement.setAttribute(name, attributes[name]);
      }
    }

    return new Promise((resolve, reject) => {
      linkElement.addEventListener('load', () => {
        resolve(linkElement);
      });
      linkElement.addEventListener('error', () => {
        reject(new Error(`Error loading stylesheet from '${attributes.href}'`));
      });
      document.head.appendChild(linkElement);
    });
  }

}

const themeLoaderConfig: ThemeLoaderConfig = {
  baseUrl: appConfig.core.polarisCdnBaseUrl,
  themes: appConfig.core.themes,
  activeTheme: userData.activeTheme,
};

export const themeLoaderService = new ThemeLoaderService(themeLoaderConfig);
